import { useCallback, type RefObject } from 'react';
import type { FieldValues } from 'react-hook-form';
import type { FormRef } from '../types';

/**
 * Custom hook for getting form values imperatively (does not subscribe to changes)
 *
 * @param formRef - Reference to the Form component
 * @returns A function to get current form values
 *
 * @example
 * ```tsx
 * const formRef = useRef<FormRef>(null);
 * const getFormValues = useFormValues(formRef);
 *
 * const handleClick = () => {
 *   const currentValues = getFormValues();
 *   console.log('Current form values:', currentValues);
 * };
 * ```
 */
export const useValues = <T = FieldValues>(
  formRef: RefObject<FormRef | null>
): ((fieldName?: string) => T | undefined) => {
  return useCallback(
    (fieldName?: string) => {
      if (!formRef.current) return undefined;
      return formRef.current.getValues<T>(fieldName);
    },
    [formRef]
  );
};

export default useValues;
